import { ref, readonly } from 'vue';
import flagsmith from 'flagsmith';
import { defaultMaxSecondaryApplicants } from './const';

export enum FeatureFlag {
  MULTIPLE_APPLICANTS = 'multiple-applicants',
  COMMERCIAL_QUOTE_FLOW = 'commercial-quote-flow',
}

// Module-level reactive state (works without Pinia)
const isInitialized = ref(false);
const isLoading = ref(true);
const hasError = ref(false);

/** In-flight initialization promise to prevent concurrent init calls */
let initPromise: Promise<void> | null = null;

const initialize = async (): Promise<void> => {
  // Already initialized - return immediately
  if (isInitialized.value) return;

  // Initialization in progress - await the existing promise
  if (initPromise) return initPromise;

  // Start new initialization
  initPromise = (async () => {
    try {
      await flagsmith.init({
        environmentID: import.meta.env.VITE_FLAGSMITH_CLIENT_KEY || '',
        cacheFlags: false,
      });
      isInitialized.value = true;
    } catch (error) {
      console.error('Failed to initialize Flagsmith:', error);
      hasError.value = true;
    } finally {
      isLoading.value = false;
      initPromise = null;
    }
  })();

  return initPromise;
};

// Initialize immediately on module load
initialize();

const isEnabled = (flagName: FeatureFlag, defaultValue = false): boolean => {
  if (import.meta.env.VITE_FLAGSMITH_ENABLE_ALL_FEATURES === 'true' && import.meta.env.MODE === 'development') {
    return true;
  }

  if (!isInitialized.value) {
    return defaultValue;
  }

  try {
    return flagsmith.hasFeature(flagName);
  } catch {
    return defaultValue;
  }
};

const getMaxSecondaryApplicants = (): number => {
  return isEnabled(FeatureFlag.MULTIPLE_APPLICANTS) ? 5 : defaultMaxSecondaryApplicants;
};

const isCommercialQuoteFlowEnabled = (): boolean => {
  return isEnabled(FeatureFlag.COMMERCIAL_QUOTE_FLOW);
};

/**
 * Composable for feature flags
 * Returns reactive state and helper methods for feature flag management
 */
export const useFeatureFlags = () => {
  return {
    // State (readonly to prevent external mutation)
    isInitialized: readonly(isInitialized),
    isLoading: readonly(isLoading),
    hasError: readonly(hasError),
    // Kept for backward compatibility
    flagsmithInitialized: readonly(isInitialized),
    // Actions
    initialize,
    isEnabled,
    getMaxSecondaryApplicants,
    isCommercialQuoteFlowEnabled,
  };
};
